import { useEffect } from 'react'
import { DELIVERY_AREA_COLORS, getZIndexForPolygonType } from '../constants'
import { getMapsUtilities } from '../utils'

/**
 * Hook for loading and displaying read-only polygons from other locations
 */
export const useOtherLocationsPolygons = ({
  isLoaded,
  maps,
  geometry,
  mapInstanceRef,
  otherLocationsPolygonsRef,
  otherLocationsPolygons,
}) => {
  useEffect(() => {
    if (!isLoaded || !maps || !geometry || !mapInstanceRef.current) {
      return
    }

    // Clear existing other location polygons
    otherLocationsPolygonsRef.current.forEach((polygon) => {
      if (polygon && polygon.setMap) {
        polygon.setMap(null)
      }
    })
    otherLocationsPolygonsRef.current = []

    if (!otherLocationsPolygons || otherLocationsPolygons.length === 0) {
      return
    }

    const utils = getMapsUtilities(maps)
    if (!utils) return

    // Sort by ID descending for consistent ordering (same as in PolygonList)
    const sortedPolygons = [...otherLocationsPolygons].sort((a, b) => {
      const aId = a.id || a.locationId || 0
      const bId = b.id || b.locationId || 0
      return bId - aId // Descending order
    })

    sortedPolygons.forEach((polygonData, index) => {
      try {
        if (!geometry || !geometry.encoding) {
          console.error('Geometry encoding library not available')
          return
        }

        const encodedPath = polygonData.encoded_polygon || polygonData
        if (!encodedPath || typeof encodedPath !== 'string') {
          console.error('Invalid encoded polygon path:', encodedPath)
          return
        }

        const path = geometry.encoding.decodePath(encodedPath)

        // Reserve index 0 for the current location's delivery boundary.
        const colorIndex = (1 + index) % DELIVERY_AREA_COLORS.length
        const color = DELIVERY_AREA_COLORS[colorIndex]

        // Create read-only polygon with distinct styling (no fill, outline only)
        const polygon = new maps.Polygon({
          paths: path,
          fillColor: color,
          fillOpacity: 0, // No fill for other locations
          strokeWeight: 2,
          strokeColor: color,
          strokeOpacity: 0.6, // Slightly transparent stroke
          clickable: false, // Not clickable
          editable: false, // Not editable
          zIndex: getZIndexForPolygonType(polygonData.polygon_type, true), // Lower z-index for other locations
        })

        polygon.setMap(mapInstanceRef.current)

        // Store metadata for reference
        polygon.polygonData = polygonData
        polygon.isReadOnly = true

        otherLocationsPolygonsRef.current.push(polygon)
      } catch (err) {
        console.error('Error decoding other location polygon:', err)
      }
    })
  }, [isLoaded, maps, geometry, mapInstanceRef, otherLocationsPolygonsRef, otherLocationsPolygons])
}
